import { useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import type { LucideIcon } from 'lucide-react'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import {
  getVisibleNavGroups,
  getAllNavDestinations,
  type NavDestination,
} from '@/config/navigationConfig'
import { NavGroup } from './NavGroup'
import { NavItem } from './NavItem'
import { useUser } from '@/contexts/UserContext'
import { useFavorites } from '@/hooks/use-favorites'
import { useRecentRoutes } from '@/hooks/use-recent-routes'
import { cn } from '@/lib/utils'

interface SidebarProps {
  collapsed: boolean
  onToggleCollapsed: () => void
}

/** Icon-only link for the collapsed rail (standalone + favorite destinations). */
function RailLink({ dest }: { dest: NavDestination }) {
  const location = useLocation()
  const isActive = location.pathname === dest.href
  const Icon = dest.icon
  return (
    <Link
      to={dest.href}
      title={dest.label}
      aria-label={dest.label}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex items-center justify-center rounded-md py-1 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        isActive
          ? 'bg-primary text-primary-foreground'
          : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
      )}
    >
      <Icon className="h-5 w-5" aria-hidden="true" />
    </Link>
  )
}

/** Which workspace a path/href belongs to — drives sidebar scoping. */
function workspaceOf(path: string): 'hr' | 'pipedrive' | 'crm' {
  if (path.startsWith('/hr')) return 'hr'
  if (path.startsWith('/pipedrive')) return 'pipedrive'
  return 'crm'
}

export function Sidebar({ collapsed, onToggleCollapsed }: SidebarProps) {
  const { isImpersonating, effectiveUserType } = useUser()
  const { favorites } = useFavorites()
  const recents = useRecentRoutes()
  const location = useLocation()

  // Scope the sidebar to the active workspace: in HR show only HR nav; in CRM
  // show everything except HR (HR is reached via the workspace switcher).
  const workspace = workspaceOf(location.pathname)
  const inWorkspace = (href: string) => workspaceOf(href) === workspace

  const filteredGroups = useMemo(
    () =>
      getVisibleNavGroups(effectiveUserType)
        .filter((g) => !g.hideFromSidebar)
        .filter((g) => (workspace === 'hr' ? g.id === 'hr' : g.id !== 'hr')),
    [effectiveUserType, workspace]
  )

  const byHref = useMemo(() => {
    const map = new Map<string, NavDestination>()
    for (const d of getAllNavDestinations(effectiveUserType)) map.set(d.href, d)
    return map
  }, [effectiveUserType])

  const favoriteDests = useMemo(
    () =>
      favorites
        .map((h) => byHref.get(h))
        .filter((d): d is NavDestination => !!d && inWorkspace(d.href)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [favorites, byHref, workspace]
  )
  const recentDests = useMemo(
    () =>
      recents
        .map((h) => byHref.get(h))
        .filter((d): d is NavDestination => !!d && inWorkspace(d.href)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [recents, byHref, workspace]
  )

  const renderGroup = (group: (typeof filteredGroups)[number]) => {
    if (group.standalone && group.href) {
      const dest: NavDestination = {
        label: group.label,
        href: group.href,
        icon: group.icon as LucideIcon,
        groupLabel: group.label,
      }
      return collapsed ? (
        <RailLink key={group.id} dest={dest} />
      ) : (
        <NavItem key={group.id} label={group.label} href={group.href} icon={group.icon} showPin />
      )
    }
    if (group.items) {
      return (
        <NavGroup
          key={group.id}
          id={group.id}
          label={group.label}
          icon={group.icon}
          items={group.items}
          defaultExpanded={group.defaultExpanded}
          collapsed={collapsed}
        />
      )
    }
    return null
  }

  return (
    <aside
      className={cn(
        'fixed left-0 bottom-0 border-r bg-background overflow-y-auto transition-[width] duration-200',
        collapsed ? 'w-16' : 'w-60',
        isImpersonating ? 'top-[116px]' : 'top-16'
      )}
      aria-label="Sidebar navigation"
    >
      {/* Sidebar toolbar: collapse toggle */}
      <div className={cn('flex px-2 pt-1', collapsed ? 'justify-center' : 'justify-end')}>
        <button
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {collapsed ? (
            <PanelLeftOpen className="h-4 w-4" aria-hidden="true" />
          ) : (
            <PanelLeftClose className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>

      <nav className="px-3 pb-4 space-y-0.5" aria-label="Primary">
        {/* Favorites */}
        {favoriteDests.length > 0 && (
          <div className="pb-0.5">
            {!collapsed && (
              <p className="px-2 pb-0.5 text-xs font-medium text-muted-foreground">Favorites</p>
            )}
            <div className="space-y-0.5">
              {favoriteDests.map((dest) =>
                collapsed ? (
                  <RailLink key={dest.href} dest={dest} />
                ) : (
                  <NavItem
                    key={dest.href}
                    label={dest.label}
                    href={dest.href}
                    icon={dest.icon}
                    showPin
                  />
                )
              )}
            </div>
          </div>
        )}

        {/* Recent (hidden in the icon rail) */}
        {!collapsed && recentDests.length > 0 && (
          <div className="pb-0.5">
            <p className="px-2 pb-0.5 text-xs font-medium text-muted-foreground">Recent</p>
            <div className="space-y-0.5">
              {recentDests.map((dest) => (
                <NavItem key={dest.href} label={dest.label} href={dest.href} icon={dest.icon} />
              ))}
            </div>
          </div>
        )}

        {(favoriteDests.length > 0 || (!collapsed && recentDests.length > 0)) && (
          <div className="my-0.5 border-t" role="separator" />
        )}

        {filteredGroups.map(renderGroup)}
      </nav>
    </aside>
  )
}
